import type { FlowDefinition, SessionAnswers } from '@fitting-room/contracts'

const NOTHING: ReadonlySet<string> = new Set()

/**
 * Which options on a step she has, in effect, already chosen on an earlier one.
 *
 * The rule is the flow document's own vocabulary and nothing else: two options
 * AGREE when they push the same attribute towards the same value. An earlier
 * answer that pushes a value AWAY agrees with nothing — a negative weight is
 * "not this", and offering it back would be the opposite of listening.
 *
 * This belongs to the client rather than to `packages/domain`: the server
 * already weighs the earlier answer on its own account, and a suggestion is a
 * seed for a control, not a ranking rule. Accepting one is a second and
 * heavier thing — her saying it herself — which is why the seed is visible.
 */
export function suggestedOptionIds(
  flow: FlowDefinition,
  choices: SessionAnswers['choices'],
  stepId: string,
): ReadonlySet<string> {
  const index = flow.steps.findIndex((s) => s.id === stepId)
  const target = index >= 0 ? flow.steps[index] : undefined
  if (!target || target.kind !== 'question' || target.suggestFrom !== 'earlier_answers') return NOTHING

  /** attributeKey -> values the screens BEFORE this one were pushed towards. */
  const wanted = new Map<string, Set<string>>()
  for (const step of flow.steps.slice(0, index)) {
    if (step.kind !== 'question') continue
    for (const optionId of choices[step.id] ?? []) {
      const option = step.options.find((o) => o.id === optionId)
      if (!option) continue
      for (const effect of option.effects) {
        if (effect.weight <= 0) continue
        let values = wanted.get(effect.attributeKey)
        if (!values) {
          values = new Set<string>()
          wanted.set(effect.attributeKey, values)
        }
        for (const value of effect.prefer) values.add(value)
      }
    }
  }

  const suggested = new Set<string>()
  for (const option of target.options) {
    const agrees = option.effects.some((effect) => {
      // Both halves have to pull the same way. An option that pushes a value
      // AWAY is not agreement with an earlier answer that wanted it — reading
      // it as one would open the screen with the tile that means "not that".
      if (effect.weight <= 0) return false
      const values = wanted.get(effect.attributeKey)
      return values !== undefined && effect.prefer.some((value) => values.has(value))
    })
    if (agrees) suggested.add(option.id)
  }
  return suggested
}
